import { useAppSelector } from '@frontend/common';
import { ConstraintType } from '@frontend/constraint/types';
import { formStore } from '@frontend/repository';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import AccountConstraintDataInput from './account-constraint-data-input/account-constraint-data-input.component';
import ApplicationConstraintDataInput from './application-constraint-data-input/application-constraint-data-input.component';
import ColliTypeConstraintDataInput from './colli-type-constraint-data-input/colli-type-constraint-data-input.component';
import { ConstraintDataByTypeInputProps } from './constraint-data-by-type-input.component';
import DestinationConstraintDataInput from './destination-constraint-data-input/destination-constraint-data-input.component';
import DimensionConstraintDataInput from './dimension-constraint-data-input/dimension-constraint-data-input.component';
import EquipmentConstraintDataInput from './equipment-constraint-data-input/equipment-constraint-data-input.component';
import EquipmentGroupConstraintDataInput from './equipment-group-constraint-data-input/equipment-group-constraint-data-input.component';
import EquipmentStateConstraintDataInput from './equipment-state-constraint-data-input/equipment-state-constraint-data-input.component';
import FeatureConstraintDataInput from './feature-constraint-data-input/feature-constraint-data-input.component';
import FunctionalityConstraintDataInput from './functionality-constraint-data-input/functionality-constraint-data-input.component';
import PackageTypeConstraintDataInput from './package-type-constraint-data-input/package-type-constraint-data-input.component';
import ProductConstraintDataInput from './product-constraint-data-input/product-constraint-data-input.component';
import ProductGroupConstraintDataInput from './product-group-constraint-data-input/product-group-constraint-data-input.component';
import ProductTypeConstraintDataInput from './product-type-constraint-data-input/product-type-constraint-data-input.component';
import StockConstraintDataInput from './stock-constraint-data-input/stock-constraint-data-input.component';
import TransactionCountConstraintDataInput from './transaction-count-constraint-data-input/transaction-count-constraint-data-input.component';
import TransactionTypeConstraintDataInput from './transaction-type-constraint-data-input/transaction-type-constraint-data-input.component';

interface ViewProps {
    content: React.ReactNode;
}

const useConstraintDataByTypeInput = (props: ConstraintDataByTypeInputProps): ViewProps => {
    const store = useAppSelector(useSelector, formStore);
    const type = store[props.formId]?.value['type'];
    const content = useMemo(() => {
        switch (type) {
            case ConstraintType.ACCOUNT:
                return <AccountConstraintDataInput {...props} />;
            case ConstraintType.APPLICATION:
                return <ApplicationConstraintDataInput {...props} />;
            case ConstraintType.DESTINATION:
                return <DestinationConstraintDataInput {...props} />;
            case ConstraintType.DIMENSION:
                return <DimensionConstraintDataInput {...props} />;
            case ConstraintType.EQUIPMENT:
                return <EquipmentConstraintDataInput {...props} />;
            case ConstraintType.EQUIPMENT_GROUP:
                return <EquipmentGroupConstraintDataInput {...props} />;
            case ConstraintType.EQUIPMENT_STATE:
                return <EquipmentStateConstraintDataInput {...props} />;
            case ConstraintType.FEATURE:
                return <FeatureConstraintDataInput {...props} />;
            case ConstraintType.FUNCTIONALITY:
                return <FunctionalityConstraintDataInput {...props} />;
            case ConstraintType.PRODUCT:
                return <ProductConstraintDataInput {...props} />;
            case ConstraintType.PRODUCT_GROUP:
                return <ProductGroupConstraintDataInput {...props} />;
            case ConstraintType.PRODUCT_TYPE:
                return <ProductTypeConstraintDataInput {...props} />;
            case ConstraintType.COLLI_TYPE:
                return <ColliTypeConstraintDataInput {...props} />;
            case ConstraintType.PACKAGE_TYPE:
                return <PackageTypeConstraintDataInput {...props} />;
            case ConstraintType.STOCK:
                return <StockConstraintDataInput {...props} />;
            case ConstraintType.TRANSACTION_COUNT:
                return <TransactionCountConstraintDataInput {...props} />;
            case ConstraintType.TRANSACTION_TYPE:
                return <TransactionTypeConstraintDataInput {...props} />;
            default:
                return <DefaultContent />;
        }
    }, [type]);

    return { content };
};

export default useConstraintDataByTypeInput;

const DefaultContent = () => {
    return <p className='ms-1'>Please select a type to fill in the details of a constraint.</p>;
};
